// checkout/utils/initRcLoqateLookup.ts

import { createLoqateInstance } from './loqate/createInstance';
import { initHiddenRows, toggleFormRow } from './toggleFormRows';

type RcScope = 'billing' | 'delivery' | 'group';

/**
 * True when we are on the review page (or anything containing "/checkout/review").
 *
 * @returns {boolean} Whether the current URL is a review page.
 */
function isReviewPage(): boolean {
	const path = window.location.pathname || '';
	return path.includes('/checkout/review');
}

/**
 * Detect which type of address the R&C modal form is currently editing.
 *
 * The legacy form includes a hidden #addressTarget that indicates billing/delivery/group.
 *
 * @param {HTMLFormElement} form The R&C modal address form element.
 * @returns {RcScope} Detected scope for the form.
 */
function detectRcScope(form: HTMLFormElement): RcScope {
	const target = form.querySelector<HTMLInputElement>('#addressTarget')?.value;

	if (target === 'billing') return 'billing';
	if (target === 'delivery') return 'delivery';
	return 'group';
}

/**
 * Try to find the country select inside the R&C modal form.
 *
 * We do not assume a single id/name, because R&C billing can be international.
 *
 * @param {HTMLFormElement} form
 * @returns {HTMLSelectElement | null}
 */
function findCountrySelect(form: HTMLFormElement): HTMLSelectElement | null {
	return (
		form.querySelector<HTMLSelectElement>('#address-country') ??
		form.querySelector<HTMLSelectElement>('#country') ??
		form.querySelector<HTMLSelectElement>('select[name="address.countryCode"]') ??
		form.querySelector<HTMLSelectElement>('select[name="countryCode"]') ??
		form.querySelector<HTMLSelectElement>('select[data-country-select]') ??
		form.querySelector<HTMLSelectElement>('select[data-pca-country]') ??
		null
	);
}

/**
 * Apply ISO2 to the country select (if present).
 *
 * @param {HTMLFormElement} form
 * @param {string} iso2
 * @returns {void}
 */
function applyCountryIso2(form: HTMLFormElement, iso2: string): void {
	const select = findCountrySelect(form);
	if (!select) return;

	// Avoid churn
	if (select.value === iso2) return;

	select.value = iso2;

	// Ensure any dependent logic runs
	select.dispatchEvent(new Event('change', { bubbles: true }));
}

/**
 * Bind Loqate to the legacy R&C modal form.
 *
 * @param {HTMLFormElement} form The R&C modal address form element.
 * @returns {void}
 */
function bindLoqateToRcModalForm(form: HTMLFormElement): void {
	// Prevent double-init if the modal content is re-rendered
	if (form.dataset.rcLoqateBound === 'true') return;
	form.dataset.rcLoqateBound = 'true';

	const scope = detectRcScope(form);
	const searchInput =
		form.querySelector<HTMLInputElement>('#address\\.search') ??
		form.querySelector<HTMLInputElement>('input[id="address.search"]') ??
		form.querySelector<HTMLInputElement>('#address-search') ??
		form.querySelector<HTMLInputElement>('input[data-pca-search]');

	if (!searchInput) return;

	const isAddress3Variant = searchInput.id === 'address-search';

	const line1 = form.querySelector<HTMLInputElement>('#address-line1');
	const line2 = form.querySelector<HTMLInputElement>('#address-line2');
	const line3 = form.querySelector<HTMLInputElement>('#address-line3');
	const town = form.querySelector<HTMLInputElement>('#address-town');
	const county = form.querySelector<HTMLInputElement>('#address-county');
	const postcode = form.querySelector<HTMLInputElement>('#address-postcode');

	if (!line1 || !town || !postcode) return;

	const manualBtn = form.querySelector<HTMLElement>('.manual-address-entry');

	// Ensure manual toggle works even if legacy JS is disabled
	if (manualBtn && manualBtn.dataset.rcManualBound !== 'true') {
		manualBtn.dataset.rcManualBound = 'true';

		manualBtn.addEventListener('click', (e) => {
			e.preventDefault();
			toggleFormRow(manualBtn);
		});

		manualBtn.addEventListener('keydown', (e: KeyboardEvent) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				manualBtn.click();
			}
		});
	}

	if (isAddress3Variant && manualBtn) {
		manualBtn.dataset.loqateRevealMode = 'click';
	}

	// Billing must not be treated as delivery
	const isDelivery = scope !== 'billing';

	// ---------------------------------------------------------------------
	// Listen for ISO2 produced by createLoqateInstance() retrieve().
	// Only apply to billing scope (international billing).
	// ---------------------------------------------------------------------
	if (scope === 'billing' && searchInput.dataset.rcCountryIso2Bound !== 'true') {
		searchInput.dataset.rcCountryIso2Bound = 'true';

		searchInput.addEventListener('loqate:countryIso2', (e: Event) => {
			const ev = e as CustomEvent<{ countryIso2?: string }>;
			const iso2 = ev?.detail?.countryIso2;

			if (!iso2) return;

			applyCountryIso2(form, iso2);
		});

		// If Loqate already set it before listener bound (edge case), apply once.
		const existing = searchInput.dataset.loqateCountryIso2;
		if (existing) {
			applyCountryIso2(form, existing);
		}
	}
	// ---------------------------------------------------------------------

	createLoqateInstance({
		searchInput,
		resultsContainer: undefined,
		manualButton: manualBtn ?? undefined,
		isDelivery,
		limit: 7,
		line1,
		line2: line2 ?? undefined,
		line3: line3 ?? undefined,
		town,
		county: county ?? undefined,
		postcode,
	});
}

/**
 * Start observing for when forms are injected/loaded.
 *
 * @param {HTMLElement} host The modal host element containing the R&C form.
 * @returns {void}
 */
function startObservingHost(host: HTMLElement): void {
	// If form already exists, bind immediately
	const initialForm = host.querySelector<HTMLFormElement>('#new-address-form');
	if (initialForm) {
		if (initialForm.dataset.rcHiddenRowsInit !== 'true') {
			initialForm.dataset.rcHiddenRowsInit = 'true';
			initHiddenRows(initialForm);
		}

		bindLoqateToRcModalForm(initialForm);
	}

	const observer = new MutationObserver(() => {
		const form = host.querySelector<HTMLFormElement>('#new-address-form');
		if (!form) return;

		if (form.dataset.rcHiddenRowsInit !== 'true') {
			form.dataset.rcHiddenRowsInit = 'true';
			initHiddenRows(form);
		}

		if (form.dataset.rcLoqateBound === 'true') return;

		bindLoqateToRcModalForm(form);
	});

	observer.observe(host, { childList: true, subtree: true });
}

/**
 * Initialise Loqate for the Review & Confirm page.
 *
 * @returns {void}
 */
export function initRcLoqateLookup(): void {
	if (!isReviewPage()) return;

	const host = document.getElementById('checkout-address-modal__address-form');
	if (!host) {
		window.addEventListener('DOMContentLoaded', () => {
			const hostAfter = document.getElementById('checkout-address-modal__address-form');
			if (!hostAfter) return;
			startObservingHost(hostAfter);
		});
		return;
	}

	startObservingHost(host);
}
